import { useQuery } from '@tanstack/react-query'
import { api } from '@/lib/api'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { ExternalLink, AlertCircle, Activity, FileText, RefreshCw, Database, ShieldAlert, DollarSign } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { EpssHealthCard } from '@/components/admin/EpssHealthCard'
import type { EpssHealthData } from '@/components/admin/EpssHealthCard'
import { DbPerfSummaryCard } from '@/components/admin/DbPerfSummaryCard'
import { LogHygieneCard } from '@/components/admin/LogHygieneCard'
import { cn } from '@/lib/utils'

interface DbHealthResponse {
  status: 'ok' | 'degraded'
  pool?: { total: number; idle: number; active: number; waiting: number; max: number }
  reason?: string
}

interface GatusEndpoint {
  name: string
  group: string
  success: boolean
  responseTime: number
  uptime7d?: number
  lastChecked: string
}

interface SentryIssue {
  id: string
  title: string
  count: number
  project: string
  firstSeen: string
  lastSeen: string
  route?: string
}

interface DbPoolStats {
  total: number
  idle: number
  waiting: number
  maxConnections: number
  utilizationPct: number
}

interface ObservabilityData {
  gatus: {
    status: 'ok' | 'unavailable'
    endpoints?: GatusEndpoint[]
    fetchedAt?: string
  }
  sentry: {
    status: 'ok' | 'unavailable'
    issues?: SentryIssue[]
    stats?: { events24h: number; newIssues24h: number }
    fetchedAt?: string
  }
  grafana: {
    status: 'ok' | 'unavailable'
    counts?: { info: number; warn: number; error: number }
    fetchedAt?: string
  }
  dbPool: DbPoolStats | null
  epss?: { flagEnabled: boolean }
  aiCost?: { totalCost30d: number; costPerArticle: number } | null
  quota?: {
    sentry: { pct?: number; status: string }
    grafana_logs_gb: { pct?: number; status: string }
  } | null
  fetchedAt: string
}

export function ObservabilityPage() {
  const { data, isLoading, refetch, dataUpdatedAt } = useQuery<ObservabilityData>({
    queryKey: ['admin', 'observability'],
    queryFn: () => api.get('/admin/observability'),
    refetchInterval: 60_000,
  })

  const { data: epssHealth } = useQuery<EpssHealthData>({
    queryKey: ['health', 'epss'],
    queryFn: async () => {
      const res = await fetch('/api/health/epss')
      return res.json() as Promise<EpssHealthData>
    },
    refetchInterval: 60_000,
  })

  // /health/db returns 200 { status:'ok' } or 500/503 { status:'degraded' }.
  // We treat any non-2xx as degraded for the chip — the endpoint is mounted
  // pre-auth at root so api.get() may need to hit it via window.fetch.
  const { data: dbHealth } = useQuery<DbHealthResponse>({
    queryKey: ['health', 'db'],
    queryFn: async () => {
      try {
        const res = await fetch('/health/db')
        const body = await res.json().catch(() => ({ status: 'degraded', reason: 'unparseable response' }))
        return body as DbHealthResponse
      } catch {
        return { status: 'degraded', reason: 'fetch failed' }
      }
    },
    refetchInterval: 30_000,
  })

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Observability</h1>
          <p className="text-sm text-muted-foreground">
            Cross-service health, errors, and log status
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={() => refetch()}>
          <RefreshCw className="h-4 w-4 mr-1" />
          Refresh
        </Button>
      </div>

      {isLoading ? (
        <div className="text-muted-foreground text-sm">Loading...</div>
      ) : !data ? (
        <div className="text-muted-foreground text-sm">Failed to load observability data</div>
      ) : (
        <div className="grid gap-6 md:grid-cols-1 lg:grid-cols-4">
          {/* DB Monitoring (alert surface) */}
          <Card data-testid="db-monitoring-card">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <ShieldAlert className="h-4 w-4" />
                DB Monitoring
              </CardTitle>
              {dbHealth && (
                <Badge
                  variant="outline"
                  className={`text-xs ${dbHealth.status === 'ok' ? 'border-green-500 text-green-500' : 'border-red-500 text-red-500'}`}
                  data-testid="db-health-chip"
                >
                  {dbHealth.status === 'ok' ? 'healthy' : 'degraded'}
                </Badge>
              )}
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <p className="text-xs text-muted-foreground">
                  Alert stack: DO infra alerts (&gt;85%/5m, &gt;95%/5m) + Grafana Loki alerts (connection exhaustion, DB restart) + app-level <code>/health/db</code> probe.
                </p>
                <div className="flex flex-col gap-2">
                  <a
                    href="https://cyberfind.grafana.net/alerting/list"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-blue-500 hover:text-blue-400 flex items-center gap-1"
                  >
                    Grafana alert list <ExternalLink className="h-3 w-3" />
                  </a>
                  <a
                    href="https://cloud.digitalocean.com/databases/c3b85799-89b3-48a8-8678-9f894aaaed47/insights"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-blue-500 hover:text-blue-400 flex items-center gap-1"
                  >
                    DO cluster insights <ExternalLink className="h-3 w-3" />
                  </a>
                  <a
                    href="/health/db"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-blue-500 hover:text-blue-400 flex items-center gap-1"
                  >
                    /health/db raw JSON <ExternalLink className="h-3 w-3" />
                  </a>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* DB Connection Pool */}
          <Card data-testid="db-pool-card">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Database className="h-4 w-4" />
                Database Connections
              </CardTitle>
            </CardHeader>
            <CardContent>
              {data.dbPool ? (
                <div className="space-y-3">
                  {(data.dbPool.waiting > 0 || data.dbPool.utilizationPct > 80) && (
                    <div
                      className="flex items-center gap-2 rounded-md border border-red-500 bg-red-500/10 px-3 py-2 text-xs text-red-500"
                      role="alert"
                      data-testid="db-pool-alert"
                    >
                      <AlertCircle className="h-3.5 w-3.5 shrink-0" />
                      DB connection pool pressure detected
                    </div>
                  )}
                  <div className="grid grid-cols-3 gap-2 text-center">
                    <div>
                      <p className="text-lg font-semibold" data-testid="db-pool-total">{data.dbPool.total}</p>
                      <p className="text-xs text-muted-foreground">total</p>
                    </div>
                    <div>
                      <p className="text-lg font-semibold" data-testid="db-pool-idle">{data.dbPool.idle}</p>
                      <p className="text-xs text-muted-foreground">idle</p>
                    </div>
                    <div>
                      <p className={`text-lg font-semibold ${data.dbPool.waiting > 0 ? 'text-red-500' : ''}`} data-testid="db-pool-waiting">{data.dbPool.waiting}</p>
                      <p className="text-xs text-muted-foreground">waiting</p>
                    </div>
                  </div>
                  <div className="space-y-1">
                    <div className="flex justify-between text-xs text-muted-foreground">
                      <span>Utilization</span>
                      <span data-testid="db-pool-utilization">{data.dbPool.utilizationPct}% of {data.dbPool.maxConnections} max</span>
                    </div>
                    <div className="h-1.5 w-full overflow-hidden rounded-full bg-secondary">
                      <div
                        className={`h-full transition-all ${data.dbPool.utilizationPct > 80 ? 'bg-red-500' : 'bg-primary'}`}
                        style={{ width: `${Math.min(data.dbPool.utilizationPct, 100)}%` }}
                      />
                    </div>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">Pool stats unavailable</p>
              )}
            </CardContent>
          </Card>

          {/* Gatus — Service Health */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Activity className="h-4 w-4" />
                Service Health
              </CardTitle>
              {data.gatus.status === 'unavailable' && (
                <Badge variant="outline" className="text-xs">Unavailable</Badge>
              )}
            </CardHeader>
            <CardContent>
              {data.gatus.status === 'ok' && data.gatus.endpoints ? (
                <div className="space-y-3">
                  {data.gatus.endpoints.map((ep) => (
                    <div key={ep.name} className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className={`h-2 w-2 rounded-full ${ep.success ? 'bg-green-500' : 'bg-red-500'}`} />
                        <span className="text-sm">{ep.name}</span>
                      </div>
                      <div className="flex items-center gap-3 text-xs text-muted-foreground">
                        <span>{ep.responseTime}ms</span>
                        {ep.uptime7d != null && <span>{ep.uptime7d}%</span>}
                      </div>
                    </div>
                  ))}
                  {data.gatus.fetchedAt && (
                    <p className="text-xs text-muted-foreground pt-1">
                      Last checked: {new Date(data.gatus.fetchedAt).toLocaleTimeString()}
                    </p>
                  )}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">Gatus not configured or unreachable</p>
              )}
            </CardContent>
          </Card>

          {/* Sentry — Errors */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <AlertCircle className="h-4 w-4" />
                Errors (Sentry)
              </CardTitle>
              <a
                href="https://cyberfindio.sentry.io"
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1"
              >
                Open <ExternalLink className="h-3 w-3" />
              </a>
            </CardHeader>
            <CardContent>
              {data.sentry.status === 'ok' ? (
                <div className="space-y-3">
                  {data.sentry.stats && (
                    <div className="flex gap-4 text-sm">
                      <span>{data.sentry.stats.newIssues24h} new issues</span>
                      <span className="text-muted-foreground">{data.sentry.stats.events24h} events (24h)</span>
                    </div>
                  )}
                  {data.sentry.issues && data.sentry.issues.length > 0 ? (
                    <div className="space-y-2">
                      {data.sentry.issues.slice(0, 5).map((issue) => (
                        <div key={issue.id} className="flex items-center justify-between text-xs">
                          <span className="truncate max-w-[200px]">{issue.title}</span>
                          <div className="flex items-center gap-2">
                            <Badge variant="secondary" className="text-xs">{issue.project}</Badge>
                            <span className="text-muted-foreground">{issue.count}x</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-green-500">No unresolved issues</p>
                  )}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">Sentry not configured or unreachable</p>
              )}
            </CardContent>
          </Card>

          {/* Sentry — Errors by Route */}
          {data.sentry.status === 'ok' && data.sentry.issues && (
            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <AlertCircle className="h-4 w-4" />
                  Errors by Route
                </CardTitle>
              </CardHeader>
              <CardContent>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-muted-foreground">
                      <th className="pb-1 font-normal">Route</th>
                      <th className="pb-1 text-right font-normal">Issues</th>
                      <th className="pb-1 text-right font-normal">Events</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(
                      data.sentry.issues.reduce<Record<string, { count: number; events: number }>>(
                        (acc, i) => {
                          const key = i.route ?? '(no route tag)'
                          acc[key] = acc[key] ?? { count: 0, events: 0 }
                          acc[key].count += 1
                          acc[key].events += i.count
                          return acc
                        },
                        {},
                      ),
                    )
                      .sort((a, b) => b[1].events - a[1].events)
                      .map(([route, stats]) => (
                        <tr key={route} className="border-t border-border/40">
                          <td className="py-1 font-mono text-xs">{route}</td>
                          <td className="py-1 text-right">{stats.count}</td>
                          <td className="py-1 text-right">{stats.events}</td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          )}

          {/* Grafana — Logs */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <FileText className="h-4 w-4" />
                Logs (Grafana)
              </CardTitle>
              <a
                href="https://cyberfind.grafana.net"
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1"
              >
                Open <ExternalLink className="h-3 w-3" />
              </a>
            </CardHeader>
            <CardContent>
              {data.grafana.status === 'ok' && data.grafana.counts ? (
                <div className="space-y-3">
                  <p className="text-sm">Last 1 hour:</p>
                  <div className="flex gap-4 text-sm">
                    <span>{data.grafana.counts.info} info</span>
                    <span className="text-yellow-500">{data.grafana.counts.warn} warn</span>
                    <span className="text-red-500">{data.grafana.counts.error} error</span>
                  </div>
                  {data.grafana.counts.error > 0 && (
                    <p className="text-xs text-red-400">Errors detected — check Grafana for details</p>
                  )}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">Grafana not configured or unreachable</p>
              )}
            </CardContent>
          </Card>

          {/* EPSS Refresh Health */}
          <EpssHealthCard
            health={epssHealth ?? null}
            flagEnabled={
              epssHealth == null
                ? null
                : epssHealth.status === 'ok' || epssHealth.status === 'stale'
            }
          />

          {/* AI Cost & Quotas */}
          <Card
            data-testid="ai-cost-quotas-card"
            className={cn(
              'border-2',
              data?.quota?.sentry.status === 'critical' || data?.quota?.grafana_logs_gb.status === 'critical'
                ? 'border-destructive'
                : data?.quota?.sentry.status === 'warn' || data?.quota?.grafana_logs_gb.status === 'warn'
                  ? 'border-amber-500'
                  : 'border-transparent',
            )}
          >
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <DollarSign className="h-4 w-4" />
                AI Cost &amp; Quotas
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              {data?.aiCost ? (
                <div className="flex flex-col gap-1">
                  <span className="text-muted-foreground text-xs">30-day spend</span>
                  <span className="font-semibold tabular-nums">${data.aiCost.totalCost30d.toFixed(2)}</span>
                  <span className="text-xs text-muted-foreground">${data.aiCost.costPerArticle.toFixed(4)} / article</span>
                </div>
              ) : (
                <span className="text-muted-foreground text-xs">AI cost data unavailable</span>
              )}
              {data?.quota ? (
                <div className="grid grid-cols-2 gap-2 pt-1">
                  <div>
                    <p className="text-xs text-muted-foreground">Sentry</p>
                    <p
                      className={`text-xs font-medium ${
                        data.quota.sentry.status === 'critical'
                          ? 'text-destructive'
                          : data.quota.sentry.status === 'warn'
                            ? 'text-amber-500'
                            : 'text-emerald-500'
                      }`}
                    >
                      {data.quota.sentry.pct != null ? `${data.quota.sentry.pct}%` : data.quota.sentry.status}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Grafana logs</p>
                    <p
                      className={`text-xs font-medium ${
                        data.quota.grafana_logs_gb.status === 'critical'
                          ? 'text-destructive'
                          : data.quota.grafana_logs_gb.status === 'warn'
                            ? 'text-amber-500'
                            : 'text-emerald-500'
                      }`}
                    >
                      {data.quota.grafana_logs_gb.pct != null
                        ? `${data.quota.grafana_logs_gb.pct}%`
                        : data.quota.grafana_logs_gb.status}
                    </p>
                  </div>
                </div>
              ) : (
                <span className="text-xs text-muted-foreground">Quota data unavailable</span>
              )}
              <a href="/admin/ai/costs" className="text-xs text-primary hover:underline block pt-1">
                View details →
              </a>
            </CardContent>
          </Card>

          {/* DB Perf summary */}
          <DbPerfSummaryCard />

          {/* Log Hygiene */}
          <LogHygieneCard />
        </div>
      )}

      {dataUpdatedAt && (
        <p className="text-xs text-muted-foreground">
          Dashboard updated: {new Date(dataUpdatedAt).toLocaleTimeString()}
        </p>
      )}
    </div>
  )
}
